import { Box, Stack, Text } from '@mantine/core';
import * as Sentry from '@sentry/react';
import {
  IconUsersGroup as AdministrationIcon,
  IconClipboardText as AuthorIcon,
  IconPresentationAnalytics as ConferenceIcon,
  IconHomeSpark as DashboardIcon,
  IconClipboardText as EvaluationsIcon,
  IconNotes as JournalIcon,
  IconScript as PublisherIcon,
  IconGraph as ReportsIcon,
  IconSitemap as ResearchCenterIcon,
  IconPlane as TravelIcon,
} from '@tabler/icons-react';
import { Outlet, createFileRoute, redirect } from '@tanstack/react-router';
import { AxiosError, isAxiosError } from 'axios';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { getCurrentUserOptions } from '@/module/Security/model/SecurityQuery';
import { UserSetup } from '@/module/Setup/UserSetup';
import { isTravelOfficeRole, travelApproverRoles, travelOfficeRoles } from '@/module/Travel/travelRoles';

import { Breadcrumbs, RootLayout } from '@/core/components';
import { useSidebarState } from '@/core/components/RootLayout/Sidebar/hooks/useSidebar';

import { FreezeDateAlert } from '@/feature/utils/components/FreezeDateAlert';
import { CurrentUser, RoleType, allRoleTypes, useCurrentUser, useCurrentUserStore } from '@feature/security';
import { useDerivePageTitle } from '@feature/utils';

const nonTravelOfficeRoles: RoleType[] = allRoleTypes.filter(role => !isTravelOfficeRole(role));

function isForbiddenResponse(error: unknown) {
  if (error == null) return false;
  return (
    isAxiosError(error) &&
    ((error as AxiosError)?.response?.status === 401 || (error as AxiosError)?.response?.status === 403)
  );
}

export const Route = createFileRoute('/_auth')({
  staticData: {
    pageTitle: { id: 'pageTitle.home' },
  },
  beforeLoad: async ({ context: { queryClient }, location, preload }) => {
    let user: CurrentUser;
    try {
      user = await queryClient.ensureQueryData(getCurrentUserOptions());
    } catch (err: unknown) {
      if (isForbiddenResponse(err)) {
        throw redirect({
          to: '/login',
          search: {
            redirect: location.href,
          },
        });
      }
      throw err;
    }

    useCurrentUserStore.getState().setCurrentUser(user);
    Sentry.setUser({
      id: user.id,
      email: user.email,
    });

    if (!preload && user.passwordChangeRequired) {
      throw redirect({ to: '/change-password', search: { redirect: location.href }, replace: true });
    }

    if (!preload && isTravelOfficeRole(user.currentRole) && !location.pathname.startsWith('/travel')) {
      throw redirect({ to: '/travel', replace: true });
    }
  },
  component: AuthLayout,
});

function SidebarHeader() {
  const { isMainActive } = useSidebarState();
  const { faculty, department, discipline } = useCurrentUser();
  if (!isMainActive) {
    return null;
  }
  return (
    <Stack
      align="center"
      gap="2px"
    >
      <Text
        ta="center"
        size="sm"
        c="grey"
      >
        {faculty?.name}
      </Text>
      <Text
        ta="center"
        size="xs"
        c="grey"
      >
        {department?.name}
      </Text>
      <Text
        ta="center"
        size="xs"
        c="grey"
        fs="italic"
      >
        {discipline?.name}
      </Text>
    </Stack>
  );
}

function AuthLayout() {
  useDerivePageTitle();
  const { t } = useTranslation();
  const { faculty, department, discipline, currentRole } = useCurrentUser();

  const sidebarItems = useMemo(
    () =>
      [
        { to: '/', label: t('sidebar.dashboard'), roles: nonTravelOfficeRoles, icon: DashboardIcon },
        {
          to: '/author',
          label: t('sidebar.author'),
          roles: [RoleType.RESEARCH_CENTER_ADMIN, RoleType.LAB_ADMIN, RoleType.UNIVERSITY_ADMIN, RoleType.AUTHOR],
          icon: AuthorIcon,
        },
        { to: '/journal', label: t('sidebar.journals'), roles: nonTravelOfficeRoles, icon: JournalIcon },
        { to: '/publisher', label: t('sidebar.publishers'), roles: nonTravelOfficeRoles, icon: PublisherIcon },
        { to: '/conference', label: t('sidebar.conferences'), roles: nonTravelOfficeRoles, icon: ConferenceIcon },
        {
          to: '/minimal-standards',
          label: t('sidebar.minimalStandards'),
          roles: [
            RoleType.UNIVERSITY_ADMIN,
            RoleType.FACULTY_ADMIN,
            RoleType.DEPARTMENT_ADMIN,
            RoleType.DISCIPLINE_ADMIN,
            RoleType.AUTHOR,
          ],
          icon: EvaluationsIcon,
        },
        {
          to: '/research-perf-eval',
          label: t('sidebar.researchPerfEval'),
          roles: [
            RoleType.UNIVERSITY_ADMIN,
            RoleType.FACULTY_ADMIN,
            RoleType.DEPARTMENT_ADMIN,
            RoleType.DISCIPLINE_ADMIN,
            RoleType.AUTHOR,
          ],
          icon: EvaluationsIcon,
        },
        {
          to: '/gov-evaluation',
          label: t('sidebar.govEvaluation'),
          roles: [
            RoleType.UNIVERSITY_ADMIN,
            RoleType.FACULTY_ADMIN,
            RoleType.DEPARTMENT_ADMIN,
            RoleType.DISCIPLINE_ADMIN,
            RoleType.AUTHOR,
          ],
          icon: EvaluationsIcon,
        },
        {
          to: '/report',
          label: t('sidebar.report'),
          roles: [
            RoleType.UNIVERSITY_ADMIN,
            RoleType.FACULTY_ADMIN,
            RoleType.DEPARTMENT_ADMIN,
            RoleType.DISCIPLINE_ADMIN,
            RoleType.AUTHOR,
          ],
          icon: ReportsIcon,
        },
        {
          to: '/evaluation',
          label: t('sidebar.evaluation'),
          roles: [
            RoleType.UNIVERSITY_ADMIN,
            RoleType.FACULTY_ADMIN,
            RoleType.DEPARTMENT_ADMIN,
            RoleType.DISCIPLINE_ADMIN,
            RoleType.AUTHOR,
          ],
          icon: EvaluationsIcon,
        },
        {
          to: '/research-center',
          label: t('sidebar.researchCenters'),
          roles: [RoleType.RESEARCH_CENTER_ADMIN, RoleType.LAB_ADMIN, RoleType.UNIVERSITY_ADMIN],
          icon: ResearchCenterIcon,
        },
        {
          to: '/travel',
          label: t('sidebar.travel'),
          roles: [
            RoleType.AUTHOR,
            RoleType.ADMIN,
            RoleType.UNIVERSITY_ADMIN,
            ...travelApproverRoles,
            ...travelOfficeRoles,
          ],
          icon: TravelIcon,
        },
        {
          to: '/administration',
          label: t('sidebar.administration'),
          roles: [
            RoleType.ADMIN,
            RoleType.UNIVERSITY_ADMIN,
            RoleType.FACULTY_ADMIN,
            RoleType.DEPARTMENT_ADMIN,
            RoleType.DISCIPLINE_ADMIN,
          ],
          icon: AdministrationIcon,
        },
      ].filter(item => item.roles == null || item.roles.includes(currentRole)),
    [t, currentRole],
  );

  // Office accounts belong to no unit, so the unit setup would trap them.
  if (!isTravelOfficeRole(currentRole) && (faculty == null || department == null || discipline == null)) {
    return <UserSetup />;
  }

  return (
    <RootLayout
      sidebarHeader={<SidebarHeader />}
      sidebarItems={sidebarItems}
    >
      <Box
        pl="md"
        mt="md"
      >
        <FreezeDateAlert />
        <Breadcrumbs />
      </Box>
      <Outlet />
    </RootLayout>
  );
}
